import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { InventoryOutputDetailModel } from 'src/app/models/inventory/inventory-output-detail.model';
import { InventoryOutputModel } from 'src/app/models/inventory/inventory-output.model';
import { RentalStockModel } from 'src/app/models/rental/rental-stock-response.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { StoreService } from 'src/app/services/archive/store.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { SalesRemissionDetailService } from 'src/app/services/inventory/sales-remission-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-sales-remission-detail',
  templateUrl: './register-sales-remission-detail.component.html',
  styleUrls: ['./register-sales-remission-detail.component.scss'],
  standalone: false
})
export class RegisterSalesRemissionDetailComponent implements OnChanges {

  @Input() salesRemissionDetailContract: InventoryOutputDetailModel = new InventoryOutputDetailModel();
  @Input() salesRemissionModel: InventoryOutputModel = new InventoryOutputModel();
  @Input() salesRemissionDetails: InventoryOutputDetailModel[] = [];
  @Input() swOrderModule: boolean = false;
  @Input() permissionUpdatable: boolean = false;
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>(false);

  codeNameArticle: string = '';
  codeNameStore: string = '';
  formSalesRemissionDetail: FormGroup = new FormGroup({});
  inventoryOutputDetailId: number = 0;
  stockArticle: number = 0;
  subscription: Subscription;

  constructor(private articleService: ArticleService,
    private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private salesRemissionDetailService: SalesRemissionDetailService,
    private storeService: StoreService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(new InventoryOutputDetailModel);
  }

  async ngOnChanges(changes: SimpleChanges) {
    const salesRemissionDetails = changes["salesRemissionDetails"];
    const salesRemissionDetailContract = changes["salesRemissionDetailContract"];
    if (salesRemissionDetails && !salesRemissionDetails.firstChange) {
      await this.createDetails(this.salesRemissionDetails);
      return;
    }
    if (salesRemissionDetailContract && !salesRemissionDetailContract.firstChange) {
      if (this.salesRemissionDetailContract.inventoryOutputDetailId == 0) {
        this.createNewDetail();
      } else {
        this.inventoryOutputDetailId = this.salesRemissionDetailContract.inventoryOutputDetailId;
        this.validateForm(this.salesRemissionDetailContract);
      }
    }
  }

  calculateQuantities(quantity: number): boolean {
    if (quantity > this.stockArticle + this.salesRemissionDetailContract.quantity) {
      this.toastHandlerService.showToast(
        'warning',
        'La cantidad no puede ser mayor a las existencias.'
      );
      return false;
    }
    this.calculateValues();
    return true;
  }

  calculateValues() {
    const form = this.formSalesRemissionDetail;
    const quantity = form.get("quantity")?.value ?? 0;
    const unitPriceSales = form.get("unitPriceSales")?.value ?? 0;
    const totalPriceSales = quantity * unitPriceSales;
    this.formSalesRemissionDetail.controls["totalPriceSales"].patchValue(totalPriceSales);
  }

  clearForm() {
    this.codeNameArticle = '';
    this.codeNameStore = '';
    this.inventoryOutputDetailId = 0;
    this.isFormView.emit(false);
    this.validateForm(new InventoryOutputDetailModel());
  }

  async configureFormControls(salesRemissionDetail: InventoryOutputDetailModel) {
    const { storeId, articleId } = salesRemissionDetail;
    const controlsToDisable: string[] = ['stock', 'totalPriceSales'];
    if (this.swOrderModule) { controlsToDisable.push('unitPriceSales'); }
    this.formControlStateService.setControlState(this.formSalesRemissionDetail, controlsToDisable, false);
    if (storeId != 0 && articleId != 0) {
      await Promise.all([
        this.getStore(storeId),
        this.getArticle(articleId, storeId)
      ]);
    }
  }

  async createDetails(salesRemissionDetails: InventoryOutputDetailModel[]) {
    try {
      await firstValueFrom(this.salesRemissionDetailService.createSomeDetails(salesRemissionDetails));
      this.toastHandlerService.showToast('success', 'Los detalles de la remisión de venta fueron creados exitosamente', '');
      this.updateListDetail.emit(true);
    } catch (error) {
      console.error('Error al crear los detalles de la remisión de venta.', error);
    }
  }

  createNewDetail() {
    this.codeNameStore = '';
    this.codeNameArticle = '';
    this.inventoryOutputDetailId = 0;
    this.validateForm(this.salesRemissionDetailContract);
  }

  async getArticle(articleId: number, storeId: number) {
    try {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      this.codeNameArticle = `${article.code} - ${article.name}`;
      await this.getStocksByArticleIdStoreId(articleId, storeId);
    } catch (error) {
      console.error('Error al obtener el articulo por Id.', error);
      this.codeNameArticle = '';
    }
  }

  async getStocksByArticleIdStoreId(articleId: number, storeId: number) {
    const rentalStockModel = new RentalStockModel();
    rentalStockModel.articleId = articleId;
    rentalStockModel.storeId = storeId;
    rentalStockModel.dateMovement = this.salesRemissionModel.dateOutput;
    try {
      const stock = await firstValueFrom(this.salesRemissionDetailService.getStocksByArticleIdStoreId(rentalStockModel));
      this.formSalesRemissionDetail.controls["stock"].patchValue(stock);
      this.stockArticle = stock;
    } catch (error) {
      this.stockArticle = 0;
      console.error('Error al buscar las existencias por artículo y bodega.', error);
    }
  }

  async getStore(storeId: number) {
    try {
      const store = await firstValueFrom(this.storeService.getStoreById(storeId));
      this.codeNameStore = `${store.code} - ${store.name}`;
    } catch (error) {
      console.error('Error al obtener la bodega por Id.', error);
      this.codeNameStore = '';
    }
  }

  async send() {
    this.validateFormControls();
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para modificar remisiones de venta. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (!this.formSalesRemissionDetail.valid) {
      this.showValidationMessages();
      return;
    }
    const modelInventoryOutputDetail = this.formSalesRemissionDetail.getRawValue() as InventoryOutputDetailModel;
    modelInventoryOutputDetail.inventoryOutputId = this.salesRemissionModel.inventoryOutputId;
    if (this.swOrderModule) {
      modelInventoryOutputDetail.orderDetailId = this.salesRemissionDetailContract.orderDetailId;
    }
    if (!this.calculateQuantities(modelInventoryOutputDetail.quantity)) {
      return;
    }
    if (this.inventoryOutputDetailId == 0) {
      try {
        await firstValueFrom(this.salesRemissionDetailService.createSalesRemissionDetail(modelInventoryOutputDetail));
        this.toastHandlerService.showToast(
          'success',
          'El detalle de la remisión de venta fue creado exitosamente.'
        );
        this.updateListDetail.emit(true);
      } catch (error) {
        console.error('Error al crear el detalle de la remisión de venta.', error);
      }
    } else {
      this.salesRemissionDetailContract.quantity = modelInventoryOutputDetail.quantity;
      this.salesRemissionDetailContract.unitPriceSales = modelInventoryOutputDetail.unitPriceSales;
      try {
        await firstValueFrom(this.salesRemissionDetailService.updateSalesRemissionDetail(this.salesRemissionDetailContract));
        this.toastHandlerService.showToast(
          'success',
          'El detalle de la remisión de venta fue modificado exitosamente.'
        );
        this.updateListDetail.emit(true);
      } catch (error) {
        console.error('Error al modificar el detalle de la remisión de venta.', error);
      }
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      storeId: {
        required: 'El campo Bodega es obligatorio.',
        min: 'Debe seleccionar una Bodega válida.'
      },
      articleId: {
        required: 'El campo Artículo es obligatorio.',
        min: 'Debe seleccionar un Artículo válido.'
      },
      quantity: {
        required: 'El campo Cantidad es obligatorio.',
        min: 'Debe ingresar una Cantidad igual o mayor a 1.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formSalesRemissionDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  validateFormControls() {
    Object.keys(this.formSalesRemissionDetail.controls).forEach((key) => {
      const control = this.formSalesRemissionDetail.get(key);
      if (control && control.invalid) {
        console.warn(
          `${key} es inválido`,
          control.errors,
          'value:',
          control.value
        );
      }
    });
  }

  async validateForm(salesRemissionDetail: InventoryOutputDetailModel) {
    this.formSalesRemissionDetail = this.formBuilder.group({
      storeId: [salesRemissionDetail.storeId, [Validators.required, Validators.min(1)]],
      articleId: [salesRemissionDetail.articleId, [Validators.required, Validators.min(1)]],
      stock: [salesRemissionDetail.stock],
      totalPriceSales: [salesRemissionDetail.quantity * salesRemissionDetail.unitPriceSales],
      quantity: [salesRemissionDetail.quantity, [Validators.required, Validators.min(1)]],
      unitPriceSales: [salesRemissionDetail.unitPriceSales, [Validators.required, Validators.min(0)]],
    });
    await this.configureFormControls(salesRemissionDetail);
  }
}
